import * as React from "react";
import { useContext, useEffect, useMemo } from "react";
import { styled } from "@mui/system";
import { ScopedCssBaseline, Theme, ThemeProvider } from "@mui/material";
import { MobileBreakpoint } from "./breakpoints";
import { useNavigation } from "../state/navigationContext";
import { PatientContent } from "./patientContent";
import { SessionRestore } from "../widget/sessionRestore";
import { usePartnerTheme } from "../hooks/usePartnerTheme";
import { useClinicData } from "../clinic/clinicHooks";
import { usePatientFeatureFlags } from "../hooks/usePatientFeatureFlags";
import { RoleRestrictor } from "fe-web-components/roles/RoleRestrictor";
import { commonBoxShadowStyle } from "fe-common/ui/boxShadow";
import { DesktopDemoZoom } from "../components/demodebug/mobileSuggestionZoom/desktopDemoZoom";
import { onRtl } from "fe-web-components/rtl/rtlStyles";
import { MainLayoutViewClassName } from "./MainLayoutViewClassName";
import { useAutoSave } from "../hooks/useAutoSave";
import { ThemeSettings } from "@kahunmedical/common-configuration/dist/configuration/theme";
import { ConfigSubObjectKeys } from "@kahunmedical/common-configuration/dist/generated";
import { UserRoles } from "fe-common/graphql";
import { ReviewPopup } from "./reviewPopup";
import { useLocaleContext } from "fe-common/locale/localeContext";
import { OverriderCssBaselineBasedOnClinic } from "./overriderCssBaselineBasedOnClinic";
import { initAlertContext } from "../state/commonStateWrapper";
import { AlertPopup, TextAlertPopUpWithTranslate } from "./AlertPopup";
import { StyledEngineProvider } from "@mui/material/styles";

//region [[ Styles ]]
const MainLayoutView = styled("div")(
  ({ theme, embedded }: { theme?: Theme; embedded: number }) => ({
    display: "flex",
    flexFlow: "row",
    width: "100%",
    // flexWrap: "wrap",
    // fontFamily: FONT_FAMILY_LATIN,
    ...onRtl({
      // fontFamily: FONT_FAMILY_HEB,
    }),
    height: "100%",
    justifyContent: "center",
    [theme.breakpoints.up(MobileBreakpoint)]: {
      background: "rgba(229, 224, 229, 1)",
      ...commonBoxShadowStyle,
    },
    left: 0,
    overFlowY: embedded === 0 ? "auto" : "hidden",
  }),
);

const MainAppLayout = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "column",
  // Give it at least 300px with
  flex: "300px",
  borderRadius: "15px",
  background: "white",
  maxWidth: "600px",
  height: "100%",
  overflowX: "hidden",
  overflowY: "auto",
  position: "relative",
  [theme.breakpoints.down(MobileBreakpoint)]: {
    width: "100%",
    borderRadius: 0,
  },
  "&.embedded": {
    width: "100%",
    maxWidth: "800px",
    borderRadius: "0",
    minHeight: "300px",
  },
}));

//endregion [[ Styles ]]

//region [[ Props ]]

export interface MainLayoutProps {
  embedded?: boolean;
  sessionId?: string;
  locale?: string;
  clinicId?: string;
  settings?: any;
  children?: React.ReactNode;
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

// the full application with patient content - TBD rename?
export const MainLayout = ({
  embedded,
  clinicId,
  ...props
}: MainLayoutProps) => {
  const { settings: settingsManager } = useClinicData();

  const { locale } = useLocaleContext();

  const alert = useContext(initAlertContext);
  const { text: errorText, textId: errorTextId } = alert || {};

  const themeSettings = useMemo(
    () =>
      settingsManager?.makeConfigObjectForKey?.(
        ThemeSettings,
        ConfigSubObjectKeys.theme,
      ),
    [settingsManager],
  );

  const { theme, settings } = usePartnerTheme(locale, themeSettings, clinicId);

  useAutoSave(clinicId);

  const { setThemeSettings } = useNavigation();

  useEffect(() => {
    if (setThemeSettings) {
      setThemeSettings(settings);
    }
  }, [setThemeSettings, settings]);

  const demoPane = usePatientFeatureFlags("demoPane") as boolean;

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <OverriderCssBaselineBasedOnClinic />
        <ScopedCssBaseline>
          <SessionRestore embedded={embedded} sessionId={props.sessionId}>
            <MainLayoutView
              className={MainLayoutViewClassName}
              embedded={embedded ? 1 : 0}
            >
              <MainAppLayout
                className={`main-app${embedded ? " embedded" : ""}`}
              >
                {errorText && (
                  <AlertPopup enableClose={false}>{errorText}</AlertPopup>
                )}
                {errorTextId && (
                  <TextAlertPopUpWithTranslate
                    enableClose={false}
                    textId={errorTextId}
                  />
                )}
                <ReviewPopup />
                <PatientContent embedded={embedded} />
              </MainAppLayout>
              <RoleRestrictor roles={[UserRoles.KahunBackofficeUsers]}>
                {!embedded && demoPane && <DesktopDemoZoom />}
              </RoleRestrictor>
            </MainLayoutView>
          </SessionRestore>
        </ScopedCssBaseline>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
